import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { browserLocalPersistence, getIdTokenResult, onIdTokenChanged, setPersistence, signInWithEmailAndPassword, signOut as firebaseSignOut, type User } from "firebase/auth";
import { collection, doc, onSnapshot, orderBy, query, runTransaction, where } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import type { PropertyListing } from "@/data/listings";
import { ListingsContext } from "@/hooks/use-listings";
import { firebaseAuth, firebaseConfigurationError, firebaseDb, firebaseStorage, isFirebaseConfigured, SUPER_ADMIN_EMAIL } from "@/integrations/firebase/client";
import { LISTING_IMAGE_LIMIT, LISTING_IMAGE_MAX_BYTES, LOCAL_LISTINGS_KEY, readLocalListings, seedListings, upsertListing, validateListing, writeLocalListings } from "@/integrations/firebase/listing-storage";

function messageFor(error: unknown): string {
  const code = typeof error === "object" && error !== null && "code" in error ? String(error.code) : "";
  if (["auth/invalid-credential", "auth/user-not-found", "auth/wrong-password", "auth/invalid-email"].includes(code)) return "E-posta veya şifre hatalı. Bilgilerinizi kontrol edin.";
  if (code === "auth/too-many-requests") return "Çok fazla giriş denemesi yapıldı. Bir süre sonra tekrar deneyin.";
  if (code === "auth/operation-not-allowed") return "Firebase Authentication içinde E-posta/Şifre giriş yöntemini etkinleştirin.";
  if (["permission-denied", "storage/unauthorized"].includes(code)) return "Firebase erişimi reddetti. Yönetici yetkisini ve Firestore/Storage kurallarını kontrol edin.";
  if (["unavailable", "auth/network-request-failed", "storage/retry-limit-exceeded"].includes(code)) return "Firebase bağlantısına ulaşılamıyor. İnternet bağlantısını kontrol edip tekrar deneyin.";
  if (code === "failed-precondition") return "Firestore kurulumu veya sorgu indeksi eksik. Yönetici kurulum belgesindeki adımları tamamlayın.";
  if (code === "storage/bucket-not-found" || code === "storage/unknown") return "Firebase fotoğraf depolaması hazır değil. Storage bucket ve yayın kurallarını kontrol edin.";
  return error instanceof Error ? error.message : "İşlem tamamlanamadı. Tekrar deneyin.";
}

async function userIsAdmin(user: User | null): Promise<boolean> {
  if (!user) return false;
  const token = await getIdTokenResult(user);
  return token.claims.cornerSuperAdmin === true || (user.emailVerified && user.email?.toLowerCase() === SUPER_ADMIN_EMAIL);
}

function initialLocalState() {
  try { return { listings: readLocalListings(window.localStorage), error: firebaseConfigurationError }; }
  catch (error) { return { listings: seedListings(), error: messageFor(error) }; }
}

async function prepareImage(file: File): Promise<Blob> {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("Fotoğraflar JPG, PNG veya WebP biçiminde olmalı.");
  if (file.size === 0 || file.size > LISTING_IMAGE_MAX_BYTES) throw new Error("Her fotoğraf en fazla 8 MB olabilir.");
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    try { await image.decode(); } catch { throw new Error("Fotoğraf okunamadı. Geçerli bir JPG, PNG veya WebP dosyası seçin."); }
    if (!image.naturalWidth || !image.naturalHeight) throw new Error("Fotoğraf boyutları okunamadı.");
    const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Tarayıcınız fotoğraf işlemeyi desteklemiyor.");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Fotoğraf hazırlanamadı.")), "image/webp", 0.86));
  } finally { URL.revokeObjectURL(url); }
}

function blobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Fotoğraf tarayıcıda okunamadı."));
    reader.readAsDataURL(blob);
  });
}

export function ListingsProvider({ children }: { children: ReactNode }) {
  const storageMode: "firebase" | "local" = isFirebaseConfigured ? "firebase" : "local";
  const [localInitial] = useState(() => storageMode === "local" ? initialLocalState() : { listings: [], error: null });
  const [allListings, setAllListings] = useState<PropertyListing[]>(localInitial.listings);
  const [error, setError] = useState<string | null>(localInitial.error);
  const [loading, setLoading] = useState(storageMode === "firebase");
  const [session, setSession] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(storageMode === "local");
  const [authReady, setAuthReady] = useState(storageMode === "local");
  const authRevision = useRef(0);

  useEffect(() => {
    // Build-time browser rendering waits for live data, including pages without
    // a visible loading panel. This marker contains no account or listing data.
    document.documentElement.dataset.listingsState = loading ? "loading" : error ? "error" : "ready";
    return () => { delete document.documentElement.dataset.listingsState; };
  }, [loading, error]);

  useEffect(() => {
    if (storageMode !== "local") return;
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LOCAL_LISTINGS_KEY && event.key !== null) return;
      try { setAllListings(readLocalListings(window.localStorage)); setError(firebaseConfigurationError); }
      catch (nextError) { setError(messageFor(nextError)); }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [storageMode]);

  useEffect(() => {
    if (!firebaseAuth || storageMode !== "firebase") return;
    return onIdTokenChanged(firebaseAuth, (user) => {
      const revision = ++authRevision.current;
      setSession(user);
      setIsAdmin(false);
      setAuthReady(false);
      // Clear private drafts immediately when the authenticated identity changes.
      setAllListings([]);
      void userIsAdmin(user).then((allowed) => {
        if (revision !== authRevision.current) return;
        setIsAdmin(allowed);
        setAuthReady(true);
      }).catch((nextError) => {
        if (revision !== authRevision.current) return;
        setError(messageFor(nextError));
        setAuthReady(true);
      });
    }, (nextError) => { setError(messageFor(nextError)); setLoading(false); setAuthReady(true); });
  }, [storageMode]);

  useEffect(() => {
    if (!firebaseDb || storageMode !== "firebase" || !authReady) return;
    setLoading(true);
    const source = collection(firebaseDb, "corner_property_listings");
    const listingQuery = isAdmin
      ? query(source, orderBy("createdAt", "desc"))
      : query(source, where("status", "==", "published"), orderBy("createdAt", "desc"));
    return onSnapshot(listingQuery, (snapshot) => {
      const next: PropertyListing[] = [];
      let invalid = false;
      snapshot.forEach((record) => {
        try {
          const listing = validateListing(record.data());
          if (listing.id !== record.id) throw new Error();
          next.push(listing);
        } catch { invalid = true; }
      });
      setAllListings(next);
      setError(invalid ? "Bazı ilan kayıtları eksik veya geçersiz olduğundan gösterilemedi." : null);
      setLoading(false);
    }, (nextError) => {
      setError(messageFor(nextError));
      setLoading(false);
    });
  }, [authReady, isAdmin, storageMode]);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!firebaseAuth) throw new Error("Yerel önizleme modunda giriş gerekmez. Gerçek yönetici girişi için Firebase yapılandırmasını tamamlayın.");
    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const result = await signInWithEmailAndPassword(firebaseAuth, email.trim().toLowerCase(), password);
      if (!await userIsAdmin(result.user)) {
        const unverified = result.user.email?.toLowerCase() === SUPER_ADMIN_EMAIL && !result.user.emailVerified;
        await firebaseSignOut(firebaseAuth);
        throw new Error(unverified ? "Yönetici e-posta adresini doğrulayın veya yetkili kurulumdan cornerSuperAdmin rolünü atayın." : "Bu hesap ilan yönetimi için yetkilendirilmemiş.");
      }
      setError(null);
    } catch (nextError) { const message = messageFor(nextError); setError(message); throw new Error(message); }
  }, []);

  const signOut = useCallback(async () => {
    if (!firebaseAuth) return;
    try { await firebaseSignOut(firebaseAuth); }
    catch (nextError) { const message = messageFor(nextError); setError(message); throw new Error(message); }
  }, []);

  const saveListing = useCallback(async (input: PropertyListing) => {
    try {
      if (!isAdmin) throw new Error("İlan kaydetmek için yetkili yönetici hesabıyla giriş yapın.");
      const listing = validateListing({ ...input, updatedAt: new Date().toISOString() });
      if (storageMode === "local") {
        const next = upsertListing(readLocalListings(window.localStorage), listing);
        writeLocalListings(window.localStorage, next);
        setAllListings(next);
      } else {
        if (!firebaseDb) throw new Error("Firebase bağlantısı hazır değil.");
        if (listing.images.some((url) => url.startsWith("data:"))) throw new Error("Canlı yayından önce fotoğrafları Firebase Storage'a yükleyin.");
        const db = firebaseDb;
        await runTransaction(db, async (transaction) => {
          const listingRef = doc(db, "corner_property_listings", listing.id);
          const slugRef = doc(db, "corner_property_listing_slugs", listing.slug);
          const [current, reservedSlug] = await Promise.all([transaction.get(listingRef), transaction.get(slugRef)]);
          if (reservedSlug.exists() && reservedSlug.data().listingId !== listing.id) throw new Error("Bu bağlantı adresi başka bir ilanda kullanılıyor.");
          const previous = current.exists() ? current.data() : null;
          const saved = { ...listing, createdAt: previous?.createdAt ?? listing.createdAt };
          if (previous?.slug && previous.slug !== saved.slug) transaction.delete(doc(db, "corner_property_listing_slugs", previous.slug));
          transaction.set(slugRef, { listingId: listing.id });
          // Firestore rejects undefined optional fields; omit them from the payload.
          transaction.set(listingRef, JSON.parse(JSON.stringify(saved)));
        });
      }
      setError(null);
    } catch (nextError) { const message = messageFor(nextError); setError(message); throw new Error(message); }
  }, [isAdmin, storageMode]);

  const deleteListing = useCallback(async (id: string) => {
    try {
      if (!isAdmin) throw new Error("İlan silmek için yetkili yönetici hesabıyla giriş yapın.");
      if (!/^[a-zA-Z0-9_-]{1,100}$/.test(id)) throw new Error("İlan kimliği geçersiz.");
      if (storageMode === "local") {
        const current = readLocalListings(window.localStorage);
        if (!current.some((listing) => listing.id === id)) throw new Error("Silinecek ilan bulunamadı.");
        const next = current.filter((listing) => listing.id !== id);
        writeLocalListings(window.localStorage, next);
        setAllListings(next);
      } else {
        if (!firebaseDb) throw new Error("Firebase bağlantısı hazır değil.");
        const db = firebaseDb;
        await runTransaction(db, async (transaction) => {
          const listingRef = doc(db, "corner_property_listings", id);
          const current = await transaction.get(listingRef);
          if (!current.exists()) throw new Error("Silinecek ilan bulunamadı.");
          if (current.data().slug) transaction.delete(doc(db, "corner_property_listing_slugs", current.data().slug));
          transaction.delete(listingRef);
        });
      }
      setError(null);
    } catch (nextError) { const message = messageFor(nextError); setError(message); throw new Error(message); }
  }, [isAdmin, storageMode]);

  const uploadImages = useCallback(async (files: File[]) => {
    const uploaded: ReturnType<typeof ref>[] = [];
    try {
      if (!isAdmin) throw new Error("Fotoğraf yüklemek için yönetici girişi yapın.");
      if (files.length > LISTING_IMAGE_LIMIT) throw new Error("Tek seferde en fazla 24 fotoğraf seçilebilir.");
      if (storageMode === "firebase" && (!firebaseStorage || !session)) throw new Error("Firebase Storage bağlantısı ve yönetici oturumu hazır olmalı.");
      const urls: string[] = [];
      for (const file of files) {
        const blob = await prepareImage(file);
        if (storageMode === "local") urls.push(await blobAsDataUrl(blob));
        else {
          const imageRef = ref(firebaseStorage!, `corner-property-images/${session!.uid}/${crypto.randomUUID()}.webp`);
          await uploadBytes(imageRef, blob, { contentType: blob.type, cacheControl: "public,max-age=31536000,immutable" });
          uploaded.push(imageRef);
          urls.push(await getDownloadURL(imageRef));
        }
      }
      setError(null);
      return urls;
    } catch (nextError) {
      // Undo this batch only; do not remove images referenced by saved listings.
      await Promise.allSettled(uploaded.map((imageRef) => deleteObject(imageRef)));
      const message = messageFor(nextError); setError(message); throw new Error(message);
    }
  }, [isAdmin, session, storageMode]);

  const value = useMemo(() => ({
    listings: allListings.filter((listing) => (listing.status ?? "published") === "published"),
    allListings, loading, storageMode, error, session, isAdmin,
    signIn, signOut, saveListing, deleteListing, uploadImages,
  }), [allListings, loading, storageMode, error, session, isAdmin, signIn, signOut, saveListing, deleteListing, uploadImages]);

  return <ListingsContext.Provider value={value}>{children}</ListingsContext.Provider>;
}
